'use client';
import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import * as _ from 'lodash';
import Cookies from 'js-cookie';
import {
  MagnifyingGlassIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowsUpDownIcon,
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DeleteIcon,
} from '@heroicons/react/24/outline';
import { RotatingLines } from 'react-loader-spinner';
import StrategyPieChart from './StrategyPieChart';
import mqtt from 'mqtt';

const TradesComponent = props => {
  const openTradesRef = useRef([]);
  const [openTrades, setOpenTrades] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);
  const [section, setSection] = useState('OpenTrades');
  const [client, setClient] = useState(null);
  const mqttClientRef = useRef(null);
  const wsRefBinance = useRef(null);
  // Store current prices by symbol for efficient updates
  const [currentPrices, setCurrentPrices] = useState({});
  // Use a ref to store live prices and throttle state updates
  const pricesRef = useRef({});

  // Subscribe on mount
  useEffect(() => {
    const handleBeforeUnload = () => {
      unsubscribeFromSymbols();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      unsubscribeFromSymbols();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);


  // Unsubscribe from Binance prices when section changes away from OpenTrades
  useEffect(() => {
    if (section !== 'OpenTrades') {
      unsubscribeFromSymbols();
    }
  }, [section]);

   useEffect(() => {
    async function fetchData() {
     
      await _connectMQTT(props);
    }
    fetchData();
  }, []);

  useEffect(() => {
    async function fetchData() {
      await getOpenTrades();
      await getClosedTrades();
   
    }
    fetchData();
  }, []);

  //Topic Execution
  useEffect(() => {
    if (!client) return;

    const handleMessage = (topic, message) => {
      if (topic === props.user.user_uuid + "_REFRESH_TRADES") {
        console.log("Refreshing Open and Closed Trades:", new Date().toLocaleTimeString());
        getOpenTrades();
        getClosedTrades();
      }
    };

    client.on('message', handleMessage);

    return () => {
      if (client) {
        client.off('message', handleMessage);
        if (mqttClientRef.current) {
          console.log('Disconnecting...', new Date().toLocaleTimeString());
          mqttClientRef.current.end();
          setClient(null);
        }
      }
    };
  }, [client, props.user.user_uuid]);

  // Sync ref with state
  useEffect(() => {
    openTradesRef.current = openTrades;
  }, [openTrades]);

  // Remove forced interval update logic (no longer needed)

  // Connect to MQTT broker
  const _connectMQTT = async (props) => {
    if (!client) {
      let clientID = 'GoldenSparrowCapital-' + Math.random().toString(16).substring(2, 8);
      const gsccookie = Cookies.get('gsccookie') || null;
      const newClient = mqtt.connect(process.env.REACT_APP_EXTERNAL_MQTT_SERVER_URL, {
        clientId: clientID,
        username: 'user-jwt',
        password: gsccookie,
        clean: true,
        rejectUnauthorized: true,
        reconnectPeriod: 60000,
      });
      mqttClientRef.current = newClient;

      newClient.on('connect', async () => {
        console.log('Connected to MQTT:', new Date().toLocaleTimeString());

        newClient.subscribe(props.user.user_uuid + "_REFRESH_TRADES", err => {
          if (!err) {
            console.log('Topic subscribed:', props.user.user_uuid + "_REFRESH_TRADES");
          } else {
            console.error('Topic Subscribe error', err);
          }
        });
      });

      setClient(newClient);
    }
  };

  // Function to subscribe to real-time Binance prices
  // Binance allows up to 50 streams per websocket connection. Batch if needed.
  const MAX_STREAMS_PER_WS = 50;
  const wsRefsBinance = useRef([]); // Array of websockets

  const subscribeToSymbols = (trades) => {
    unsubscribeFromSymbols();
    if (!trades || trades.length === 0) {
      console.log("No trades to subscribe to");
      return;
    }
    // Batch trades into groups of 50
    const batches = [];
    for (let i = 0; i < trades.length; i += MAX_STREAMS_PER_WS) {
      batches.push(trades.slice(i, i + MAX_STREAMS_PER_WS));
    }
    wsRefsBinance.current = batches.map((batch, idx) => {
      const streams = batch.map(w => `${w.symbol.toLowerCase()}@bookTicker`).join('/');
      const url = `wss://stream.binance.com:9443/stream?streams=${streams}`;
      const ws = new WebSocket(url);
      ws.onopen = () => {
        console.log(`Binance WebSocket connected (batch ${idx + 1}/${batches.length})`);
      };
      ws.onmessage = event => {
        const message = JSON.parse(event.data);
        const symbol = message.data.s;
        const bidPrice = message.data.b;
        const askPrice = message.data.a;
        // Only update the price for the relevant symbol in the ref
        const trade = openTradesRef.current.find(item => item.symbol.toUpperCase() === symbol.toUpperCase());
        if (!trade) return;
        const newPrice = trade.openingPosition === 'BUY'
          ? Number(askPrice).toFixed(trade.numberOfDecimalValues)
          : Number(bidPrice).toFixed(trade.numberOfDecimalValues);
        if (pricesRef.current[symbol] !== newPrice) {
          pricesRef.current[symbol] = newPrice;
        }
        // Do NOT call setCurrentPrices here!
      };
      ws.onerror = err => {
        console.error(`Binance WebSocket error (batch ${idx + 1}):`, err);
        if (openTradesRef.current.length > 0) {
          console.log(`Attempting to reconnect WebSocket (batch ${idx + 1})...`);
          subscribeToSymbols(openTradesRef.current);
        }
      };
      ws.onclose = () => {
        console.log(`Binance WebSocket closed (batch ${idx + 1})`);
        if (openTradesRef.current.length > 0) {
          console.log(`Attempting to reconnect WebSocket (batch ${idx + 1})...`);
          subscribeToSymbols(openTradesRef.current);
        }
      };
      return ws;
    });
  };
  // Throttle price updates to state (every 1s)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPrices({ ...pricesRef.current });
    }, 1000);
    return () => clearInterval(interval);
  }, []);


  // Function to unsubscribe/cleanup
  const unsubscribeFromSymbols = () => {
    if (wsRefsBinance.current && wsRefsBinance.current.length > 0) {
      wsRefsBinance.current.forEach(ws => {
        if (ws) ws.close();
      });
      wsRefsBinance.current = [];
      console.log('Unsubscribed from all Binance price websockets');
    }
  };

  const getOpenTrades = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/dashboard/getOpenTrades`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        setOpenTrades([]);
        setCurrentPrices({});
        pricesRef.current = {};
        return;
      }

      const data = await response.json();
      
      if (data.success && Array.isArray(data.data)) {
        //set open trades where closingPosition is null
        const openTrades = data.data.filter(trade => trade.closingPosition === null);
        setOpenTrades(openTrades);
        // Reset prices for new trades
        setCurrentPrices({});
        pricesRef.current = {};
        subscribeToSymbols(openTrades);
      } else {
        console.error('Invalid response format:', data.message || 'Unknown error');
      }
    } catch (e) {
      console.error('Error fetching open trades:', e);
    }
  };

  const getClosedTrades = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/dashboard/getClosedTrades`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        setClosedTrades([]);
        return;
      }

      const data = await response.json();
      
      if (data.success && Array.isArray(data.data)) {
         const closedTrades = data.data.filter(trade => trade.closingPosition !== null);
        setClosedTrades(closedTrades);
        // closedTradesRef.current = closedTrades;
      } else {
        console.error('Invalid response format:', data.message || 'Unknown error');
      }
    } catch (e) {
      console.error('Error fetching closed trades:', e);
    }
  };

  // Helper: Aggregate profit/loss by strategy
  const aggregateStrategyPnL = (trades, isClosed) => {
    const stratMap = {};
    trades.forEach(trade => {
      const strat = trade.strategyName || 'No Strategy';
      let profitLoss = 0;
      if (isClosed) {
        profitLoss = (trade?.quantity * Number(trade.closingPositionPrice) - trade?.quantity * Number(trade.openingPositionPrice));
      } else {
        // Use currentPrices for open trades
        const price = currentPrices[trade.symbol] || trade.currentPrice;
        if (price) {
          profitLoss = trade?.quantity * (Number(price) - (trade.openingPosition === "BUY" ? Number(trade.openingPositionPrice) : Number(trade.closingPositionPrice)));
        }
      }
      if (!stratMap[strat]) stratMap[strat] = { profit: 0, loss: 0 };
      if (profitLoss >= 0) {
        stratMap[strat].profit += profitLoss;
      } else {
        stratMap[strat].loss += profitLoss;
      }
    });
    return stratMap;
  };

  // Memoize open trades with current prices for rendering
  const openTradesWithPrices = React.useMemo(() => {
    return openTrades.map(trade => {
      const price = currentPrices[trade.symbol] || trade.currentPrice;
      return { ...trade, currentPrice: price };
    });
  }, [openTrades, currentPrices]);

  // Memoized table row to avoid unnecessary re-renders
  const TradeRow = memo(({ trade, index, userType }) => {
    const profitLoss = trade?.currentPrice
      ? (
          trade?.quantity *
          (Number(trade?.currentPrice) - 
            (trade.openingPosition === "BUY" ? Number(trade.openingPositionPrice) : Number(trade.closingPositionPrice)))
        ).toFixed(2)
      : null;
    return (
      <tr key={trade.position_uuid}>
        <td className="text-xs py-2 px-4 border-b text-center">
          {index + 1}.
        </td>
        <td className="text-xs py-2 px-4 border-b text-center">
          {new Date(trade.openingPositionDateTime).toLocaleDateString() + ', ' +
            new Date(trade.openingPositionDateTime).toLocaleTimeString(undefined, { hour12: true })}
        </td>
        <td className="py-2 px-4 border-b">
          <div style={{ alignItems: 'center', display: 'flex' }}>
            <button
              style={{
                backgroundColor: '#0474c9',
                fontSize: 12,
                borderRadius: 5,
                padding: 5,
              }}
            >
              <div style={{ alignItems: 'center', display: 'flex' }}>
                <h4 style={{ fontWeight: 'bolder', paddingLeft: 2 }}>{trade.symbol}</h4>
                {trade?.currentPrice && (
                  <h6 style={{ paddingLeft: 5 }}>
                    ${parseFloat(trade?.currentPrice).toFixed(trade?.numberOfDecimalValues)}
                  </h6>
                )}
              </div>
            </button>
            {userType == 'ADMIN' ?
              <a
                href={`https://www.coinglass.com/tv/Binance_${trade.symbol}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginLeft: 6, display: 'flex', alignItems: 'center' }}
                title="CoinGlass"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 32 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ display: 'inline', verticalAlign: 'middle' }}
                >
                  <circle cx="16" cy="16" r="16" fill="#1E222D" />
                  <path
                    d="M10.5 21.5L21.5 10.5M21.5 10.5H13.5M21.5 10.5V18.5"
                    stroke="#ffffff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="16" cy="16" r="15.5" stroke="#2A2E39" strokeWidth="1" />
                </svg>
              </a>
              : null}
            <a
              href={`https://www.tradingview.com/chart/?symbol=BINANCE%3A${trade.symbol}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginLeft: 6, display: 'flex', alignItems: 'center' }}
              title="TradingView"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ display: 'inline', verticalAlign: 'middle' }}
              >
                <circle cx="16" cy="16" r="16" fill="#1E222D" />
                <rect x="9" y="15" width="3" height="7" rx="1" fill="#ffffff" />
                <rect x="14.5" y="10" width="3" height="12" rx="1" fill="#ffffff" />
                <rect x="20" y="13" width="3" height="9" rx="1" fill="#ffffff" />
                <circle cx="16" cy="16" r="15.5" stroke="#2A2E39" strokeWidth="1" />
              </svg>
            </a>
          </div>
        </td>
        {userType === 'ADMIN' && (
          <td className="text-xs py-2 px-4 border-b text-center">
            {trade.strategyName}
          </td>
        )}
        <td className="text-xs py-2 px-4 border-b text-center">
          {trade.openingPosition === "BUY" ? "Bought" : "Sold"} ( {Number(trade.quantity).toFixed(2)} @ ${Number(trade.openingPositionPrice).toFixed(trade?.numberOfDecimalValues)} )
        </td>
        <td className="text-xs py-2 px-4 border-b text-center" style={{ color: "grey" }}>
          ${Number(trade?.stopLossPrice).toFixed(trade.numberOfDecimalValues)}
        </td>
        <td className="text-xs py-2 px-4 border-b text-center">
          {profitLoss !== null ? (
            <span style={{
              borderRadius: 5,
              padding: 5,
              color: "#fff",
              backgroundColor: parseFloat(profitLoss) >= 0 ? "green" : "red"
            }}>
              ${profitLoss}
            </span>
          ) : (
            <span style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}>
              <RotatingLines
                visible={true}
                height="22"
                width="22"
                color="grey"
                strokeWidth="3"
                animationDuration="1"
                ariaLabel="rotating-lines-loading"
              />
            </span>
          )}
        </td>
      </tr>
    );
  });

  TradeRow.displayName = 'TradeRow';

  const openStrategyPnL = aggregateStrategyPnL(openTradesWithPrices, false);
  const closedStrategyPnL = aggregateStrategyPnL(closedTrades, true);

  return (
    <div style={{ borderRadius: 8, width: 'auto' }}>
      <div style={{ marginTop: 5, display: 'flex', gap: 3 }}>
        <button
          style={{
            backgroundColor: section === "OpenTrades" ? '#fff' : "gray",
            color: section === "OpenTrades" ? "#282828" : "#fff",
            borderTopRightRadius: 8,
            borderTopLeftRadius: 8,
            padding: 6,
            fontSize: 14
          }}
          onClick={() => {
            setSection('OpenTrades');
            getOpenTrades();
          }}
        >
          Open Trades ({openTrades.length})
        </button>
        <button
          style={{
            backgroundColor: section === "ClosedTrades" ? '#fff' : "gray",
            color: section === "ClosedTrades" ? "#282828" : "#fff",
            borderTopRightRadius: 8,
            borderTopLeftRadius: 8,
            padding: 6,
            fontSize: 14
          }}
          onClick={() => {
            setSection('ClosedTrades');
            getClosedTrades();
          }}
        >
          Closed Trades
          {closedTrades.length > 0 && ` (${closedTrades.length})`}
        </button>
      </div>
      <hr></hr>

      {section === "OpenTrades" ? (
        <div>
          {/* Pie charts for each strategy */}
          {openTradesWithPrices.length > 0 && (
            <div style={{ margin: '10px 0', fontWeight: 'bold', fontSize: 16 }}>
              Total P/L: $
              {Object.values(openStrategyPnL)
                .reduce((acc, { profit, loss }) => acc + profit + loss, 0)
                .toFixed(2)}
            </div>
          )}
          {openTradesWithPrices.length > 0 ? 
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, margin: 10 }}>
            {Object.entries(openStrategyPnL).map(([strategy, { profit, loss }]) => (
              <StrategyPieChart
                key={strategy}
                strategyName={strategy}
                profit={profit}
                loss={loss}
              />
            ))}
          </div> : null }
          {openTradesWithPrices.length > 0 ? (
            <div style={{ padding: 5, marginTop : 25 }}>
              <table className="min-w-full bg-white dark:bg-gray-800 shadow-md rounded-lg">
                <thead>
                  <tr>
                    <th className="text-sm py-2 px-4 border-b">S.No.</th>
                    <th className="text-sm py-2 px-4 border-b">Opening Position Date & Time</th>
                    <th className="text-sm py-2 px-4 border-b">Symbol</th>
                    {props.user?.userType === 'ADMIN' && (
                      <th className="text-sm py-2 px-4 border-b">Strategy Name</th>
                    )}
                    <th className="text-sm py-2 px-4 border-b">Opening Position (Quantity @ Price)</th>

                    {/* <th className="text-sm py-2 px-4 border-b">Position Price</th> */}
                    <th className="text-sm py-2 px-4 border-b">Stop Loss Price</th>
                   
                    <th className="text-sm py-2 px-4 border-b">Profit/Loss</th>
                  </tr>
                </thead>
                <tbody>
                  {_.orderBy(openTradesWithPrices, 'creationDate', 'desc').map((trade, index) => (
                    <TradeRow trade={trade} index={index} userType={props.user?.userType} key={trade.position_uuid} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: 5 }}>
              <h5 style={{ margin: 0, padding: 0, color: 'red', fontSize: 12 }}>No open trades yet.</h5>
            </div>
          )}
        </div>
      ) : null}

      {section === "ClosedTrades" ? (
      <div style={{ padding: 5, marginTop : 10 }}>
          {/* Pie charts for each strategy */}
           {closedTrades.length > 0 ?
           <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, margin: 10 }}>
           {Object.entries(closedStrategyPnL).map(([strategy, { profit, loss }]) => (
              <StrategyPieChart
                key={strategy}
                strategyName={strategy}
                profit={profit}
                loss={loss}
              />
            ))}
          </div> : null }
          {closedTrades.length > 0 ? (
           <div style={{ padding: 5, marginTop : 25 }}>
              <table className="min-w-full bg-white dark:bg-gray-800 shadow-md rounded-lg">
                <thead>
                  <tr>
                    <th className="text-sm py-2 px-4 border-b">S.No.</th>
                    <th className="text-sm py-2 px-4 border-b">Open Date & Time</th>
                    <th className="text-sm py-2 px-4 border-b">Closed Date & Time</th>
                    <th className="text-sm py-2 px-4 border-b">Symbol</th>
                    {props.user?.userType === 'ADMIN' && (
                      <th className="text-sm py-2 px-4 border-b">Strategy Name</th>
                    )}
                    <th className="text-sm py-2 px-4 border-b">Opening Position</th>
                    <th className="text-sm py-2 px-4 border-b">Closing Position</th>
                   
                    <th className="text-sm py-2 px-4 border-b">Profit/Loss</th>
                     {props.user?.userType === 'ADMIN' && (
                      <th className="text-sm py-2 px-4 border-b">Reason</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {_.orderBy(closedTrades, 'closingPositionDateTime', 'desc').map((trade, index) => {
                    const profitLoss = trade.openingPosition == "BUY" ? (
                      trade?.quantity * Number(trade.closingPositionPrice) -
                      trade?.quantity * Number(trade.openingPositionPrice)
                    ).toFixed(2) : (
                      trade?.quantity * Number(trade.openingPositionPrice) -
                      trade?.quantity * Number(trade.closingPositionPrice)
                    ).toFixed(2);

                    return (
                      <tr key={trade.position_uuid}>
                        <td className="text-xs py-2 px-4 border-b text-center">
                          {index + 1}.
                        </td>
                        <td className="text-xs py-2 px-4 border-b text-center">
                          {new Date(trade.openingPositionDateTime).toLocaleDateString() + ', ' +
                           new Date(trade.openingPositionDateTime).toLocaleTimeString(undefined, { hour12: true })}
                        </td>
                        <td className="text-xs py-2 px-4 border-b text-center">
                          {new Date(trade.closingPositionDateTime).toLocaleDateString() + ', ' +
                           new Date(trade.closingPositionDateTime).toLocaleTimeString(undefined, { hour12: true })}
                        </td>
                        <td className="py-2 px-4 border-b">
                          <button
                            style={{
                              backgroundColor: 'grey',
                              fontSize: 12,
                              borderRadius: 5,
                              padding: 5,
                            }}
                            onClick={() =>
                              window.open(
                                'https://www.tradingview.com/chart/?symbol=BINANCE%3A' + trade.symbol,
                                '_blank'
                              )
                            }
                          >
                            <h4 style={{ fontWeight: 'bolder', paddingLeft: 2 }}>{trade.symbol}</h4>
                          </button>
                        </td>
                        {props.user?.userType === 'ADMIN' && (
                          <td className="text-xs py-2 px-4 border-b text-center">
                            {trade.strategyName}
                          </td>
                        )}
                        <td className="text-xs py-2 px-4 border-b text-center">
                          {trade.openingPosition == "BUY" ? "BOUGHT" : "SOLD"} ({Number(trade.quantity).toFixed(2)} @ ${Number(trade?.openingPositionPrice).toFixed(trade.numberOfDecimalValues)})
                        </td>
                        <td className="text-xs py-2 px-4 border-b text-center">
                           {trade.closingPosition == "BUY" ? "BOUGHT" : "SOLD"} (${Number(trade?.closingPositionPrice).toFixed(trade.numberOfDecimalValues)})
                        </td>
                       
                        <td className="text-xs py-2 px-4 border-b text-center">
                          <span style={{
                            borderRadius: 5,
                            padding: 5,
                            color: "#fff",
                            backgroundColor: parseFloat(profitLoss) > 0 ? 'green' : 'red'
                          }}>
                            ${profitLoss}
                          </span>
                        </td>
                          {props.user?.userType === 'ADMIN' && (
                            <td className="text-xs py-2 px-4 border-b text-center">
                              {trade.reason}
                            </td>
                          )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: 5 }}>
              <h5 style={{ margin: 0, padding: 0, color: 'red', fontSize: 12 }}>No closed trades yet.</h5>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default TradesComponent;